import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAnimation } from '../contexts/AnimationContext';
import api, { formatApiError, isAuthenticated, getTenantSubdomain } from '../services/api';
import './Login.css';

const ResetPassword = () => {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const { useTransformersAnimation } = useAnimation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Detect flow type: email reset (has uid/token) or change password (authenticated)
  const uid = searchParams.get('uid');
  const token = searchParams.get('token');
  const isEmailResetFlow = !!(uid && token);
  const isChangePasswordFlow = !isEmailResetFlow && isAuthenticated();
  
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [oldPasswordFocused, setOldPasswordFocused] = useState(false);
  const [newPasswordFocused, setNewPasswordFocused] = useState(false);
  const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);
  const oldPasswordInputRef = useRef(null);
  const isRsTenant = (getTenantSubdomain() || '').toLowerCase() === 'rs';
  const authAccent = isRsTenant ? '#2a2a2a' : '#0045f5';
  const authFocusShadow = isRsTenant
    ? 'focus:shadow-[0_0_0_1px_rgba(42,42,42,1)]'
    : 'focus:shadow-[0_0_0_1px_rgba(0,69,245,1)]';

  const handleOldPasswordFocus = () => {
    setOldPasswordFocused(true);
  };

  const handleOldPasswordBlur = () => {
    setOldPasswordFocused(false);
  };

  const handleNewPasswordFocus = () => {
    setNewPasswordFocused(true);
  };

  const handleNewPasswordBlur = () => {
    setNewPasswordFocused(false);
  };

  const handleConfirmPasswordFocus = () => {
    setConfirmPasswordFocused(true);
  };

  const handleConfirmPasswordBlur = () => {
    setConfirmPasswordFocused(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setFieldErrors({});
    setIsSuccess(false);

    // Client-side validation
    if (newPassword !== confirmPassword) {
      setFieldErrors({ confirm_password: [t('passwordsDoNotMatch')] });
      setIsLoading(false);
      return;
    }

    if (isChangePasswordFlow && oldPassword === newPassword) {
      setFieldErrors({ new_password: [t('newPasswordMustDiffer')] });
      setIsLoading(false);
      return;
    }

    try {
      if (isEmailResetFlow) {
        // Reset password via email link
        await api.resetPasswordConfirm(uid, token, newPassword, confirmPassword);
      } else if (isChangePasswordFlow) {
        // Change password (authenticated)
        await api.changePassword(oldPassword, newPassword, confirmPassword);
      } else {
        // User is not authenticated and no reset link - redirect to login
        navigate('/login');
        return;
      }
      
      // Show success message
      setIsSuccess(true);
      
      // Redirect based on flow type
      setTimeout(() => {
        if (isEmailResetFlow) {
          // Email reset flow - redirect to login (user not authenticated)
          navigate('/login');
        } else if (isChangePasswordFlow) {
          // Change password flow - redirect to dashboard (user is authenticated)
          navigate('/admin-dashboard');
        } else {
          // Fallback to login
          navigate('/login');
        }
      }, 2000);
    } catch (err) {
      const formattedError = formatApiError(err);
      setFieldErrors(formattedError.fieldErrors);
      setError(formattedError.generalError);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleOldPasswordVisibility = () => {
    setShowOldPassword(!showOldPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Focus on appropriate input after animation completes (only for transformers animation)
  useEffect(() => {
    if (useTransformersAnimation) {
      const timer = setTimeout(() => {
        if (isChangePasswordFlow && oldPasswordInputRef.current) {
          oldPasswordInputRef.current.focus();
        }
      }, 600); // Wait for form container animation (0.5s) + small buffer

      return () => clearTimeout(timer);
    }
  }, [useTransformersAnimation, isChangePasswordFlow]);

  // Render form container based on animation mode
  const FormContainer = useTransformersAnimation ? motion.div : 'div';
  const formContainerProps = useTransformersAnimation ? {
    layoutId: "form-container",
    layout: false,
    style: { willChange: 'height' }
  } : {};

  // Render header based on animation mode
  const HeaderContainer = useTransformersAnimation ? motion.div : 'div';
  const headerProps = useTransformersAnimation ? {
    layoutId: "header",
    layout: false,
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.35, ease: 'easeOut' },
    style: { willChange: 'opacity' }
  } : {};

  // Render input wrapper based on animation mode
  const InputWrapper = useTransformersAnimation ? motion.div : 'div';
  const getInputWrapperProps = (fieldName, delay) => useTransformersAnimation ? {
    layoutId: `${fieldName}-input`,
    layout: false,
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3, delay, ease: 'easeInOut' },
    style: { willChange: 'opacity' }
  } : {};

  // Render button based on animation mode
  const ButtonWrapper = useTransformersAnimation ? motion.button : 'button';
  const buttonProps = useTransformersAnimation ? {
    layoutId: "submit-button",
    layout: false,
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3, delay: 0.3, ease: 'easeInOut' },
    style: { 
      backgroundColor: '#0045f5',
      willChange: 'opacity' 
    }
  } : {
    style: { backgroundColor: '#0045f5' }
  };

  return (
    <>
      <main className="relative z-10 w-full px-0 py-0 md:px-4 md:py-12 md:max-w-[800px] h-screen md:h-auto">
        <div className="relative p-[2px] rounded-none md:rounded-2xl transition-all duration-500 w-full h-full md:h-auto transform group/card scale-100 md:scale-[0.8]" style={{ backgroundColor: authAccent, transformOrigin: 'center center' }}>
          <FormContainer className="glass-card rounded-none md:rounded-2xl p-6 md:p-10 w-full h-full relative z-10 flex flex-col overflow-y-auto" {...formContainerProps}>
            <HeaderContainer className="text-center mb-2" {...headerProps}>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight mt-8 md:mt-0">{t('resetTitle')}</h1>
              <p className="text-gray-500 text-base">
                {isEmailResetFlow 
                  ? t('resetSubtitleEmail')
                  : t('resetSubtitleChange')}
              </p>
            </HeaderContainer>

            {/* Success message */}
            {isSuccess && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-green-600">check_circle</span>
                  <div>
                    <p className="text-sm font-semibold text-green-900 mb-1">{t('passwordResetSuccessTitle')}</p>
                    <p className="text-xs text-green-700">{t('passwordResetSuccessBody')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Error message */}
            {error && !isSuccess && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-5 flex-grow">
              {/* Old Password input - only show for change password flow */}
              {isChangePasswordFlow && (
                <InputWrapper className="group relative transition-all duration-300" {...getInputWrapperProps('oldPassword', 0.05)}>
                  <label 
                    className={`block text-sm font-semibold text-nokia-blue mb-1.5 ml-1 transition-all duration-300 opacity-0 -translate-y-2 group-focus-within:opacity-100 group-focus-within:translate-y-0`} 
                    htmlFor="oldPassword"
                  >
                    {t('oldPassword')}
                  </label>
                  <div className="relative flex items-center">
                    <span className="material-symbols-outlined absolute left-4 text-gray-400 transition-colors group-focus-within:text-nokia-blue z-10">lock</span>
                    <input
                      ref={oldPasswordInputRef}
                      className={`w-full bg-white border-2 ${
                        fieldErrors.old_password ? 'border-red-500' : 'border-gray-300'
                      } text-gray-900 text-base rounded-lg focus:ring-0 focus:border-nokia-blue block p-4 pl-12 pr-12 placeholder-gray-400 transition-all duration-300 ease-out hover:border-gray-400 ${authFocusShadow} h-12`}
                      id="oldPassword"
                      type={showOldPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={oldPassword}
                      onChange={(e) => {
                        setOldPassword(e.target.value);
                        if (fieldErrors.old_password) {
                          setFieldErrors({ ...fieldErrors, old_password: null });
                        }
                      }}
                      onFocus={handleOldPasswordFocus}
                      onBlur={handleOldPasswordBlur}
                      required
                      disabled={isLoading || isSuccess}
                    />
                    <button
                      type="button"
                      className="absolute right-4 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                      onClick={toggleOldPasswordVisibility}
                      aria-label={showOldPassword ? tCommon('hidePassword') : tCommon('showPassword')}
                      disabled={isLoading || isSuccess}
                    >
                      <span className="material-symbols-outlined">
                        {showOldPassword ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                  </div>
                  {fieldErrors.old_password && (
                    <p className="mt-1 text-sm text-red-600 ml-1">{fieldErrors.old_password[0]}</p>
                  )}
                </InputWrapper>
              )}

              {/* New Password input */}
              <InputWrapper className="group relative transition-all duration-300" {...getInputWrapperProps('newPassword', 0.1)}>
                <label 
                  className={`block text-sm font-semibold text-nokia-blue mb-1.5 ml-1 transition-all duration-300 opacity-0 -translate-y-2 group-focus-within:opacity-100 group-focus-within:translate-y-0`} 
                  htmlFor="newPassword"
                >
                  {t('newPassword')}
                </label>
                <div className="relative flex items-center">
                  <span className="material-symbols-outlined absolute left-4 text-gray-400 transition-colors group-focus-within:text-nokia-blue z-10">lock</span>
                  <input
                    className={`w-full bg-white border-2 ${
                      fieldErrors.new_password ? 'border-red-500' : 'border-gray-300'
                    } text-gray-900 text-base rounded-lg focus:ring-0 focus:border-nokia-blue block p-4 pl-12 pr-12 placeholder-gray-400 transition-all duration-300 ease-out hover:border-gray-400 ${authFocusShadow} h-12`}
                      id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      if (fieldErrors.new_password) {
                        setFieldErrors({ ...fieldErrors, new_password: null });
                      }
                    }}
                    onFocus={handleNewPasswordFocus}
                    onBlur={handleNewPasswordBlur}
                    required
                    disabled={isLoading || isSuccess}
                  />
                  <button
                    type="button"
                    className="absolute right-4 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                    onClick={toggleNewPasswordVisibility}
                    aria-label={showNewPassword ? tCommon('hidePassword') : tCommon('showPassword')}
                    disabled={isLoading || isSuccess}
                  >
                    <span className="material-symbols-outlined">
                      {showNewPassword ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
                {fieldErrors.new_password && (
                  <p className="mt-1 text-sm text-red-600 ml-1">{fieldErrors.new_password[0]}</p>
                )}
              </InputWrapper>

              {/* Confirm Password input */}
              <InputWrapper className="group relative transition-all duration-300" {...getInputWrapperProps('confirmPassword', 0.15)}>
                <label 
                  className={`block text-sm font-semibold text-nokia-blue mb-1.5 ml-1 transition-all duration-300 opacity-0 -translate-y-2 group-focus-within:opacity-100 group-focus-within:translate-y-0`} 
                  htmlFor="confirmPassword"
                >
                  {t('confirmNewPassword')}
                </label>
                <div className="relative flex items-center">
                  <span className="material-symbols-outlined absolute left-4 text-gray-400 transition-colors group-focus-within:text-nokia-blue z-10">lock</span>
                  <input
                    className={`w-full bg-white border-2 ${
                      fieldErrors.confirm_password ? 'border-red-500' : 'border-gray-300'
                    } text-gray-900 text-base rounded-lg focus:ring-0 focus:border-nokia-blue block p-4 pl-12 pr-12 placeholder-gray-400 transition-all duration-300 ease-out hover:border-gray-400 ${authFocusShadow} h-12`}
                      id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (fieldErrors.confirm_password) {
                        setFieldErrors({ ...fieldErrors, confirm_password: null });
                      }
                    }}
                    onFocus={handleConfirmPasswordFocus}
                    onBlur={handleConfirmPasswordBlur}
                    required
                    disabled={isLoading || isSuccess}
                  />
                  <button
                    type="button"
                    className="absolute right-4 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                    onClick={toggleConfirmPasswordVisibility}
                    aria-label={showConfirmPassword ? tCommon('hidePassword') : tCommon('showPassword')}
                    disabled={isLoading || isSuccess}
                  >
                    <span className="material-symbols-outlined">
                      {showConfirmPassword ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
                {fieldErrors.confirm_password && (
                  <p className="mt-1 text-sm text-red-600 ml-1">{fieldErrors.confirm_password[0]}</p>
                )}
              </InputWrapper>

              {/* Submit button */}
              <ButtonWrapper
                className={`relative overflow-hidden w-full text-white font-bold rounded-lg py-3 md:py-4 px-4 md:px-6 mt-6 transition-all duration-300 transform active:scale-[0.98] group border border-transparent ${
                  isLoading || isSuccess ? 'opacity-75 cursor-not-allowed' : ''
                }`}
                {...buttonProps}
                type="submit"
                disabled={isLoading || isSuccess}
              >
                <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer"></div>
                <span className="relative z-10 flex items-center justify-center gap-2 tracking-wide text-sm md:text-base">
                  {isSuccess ? (
                    <>
                      <span className="material-symbols-outlined">check_circle</span>
                      {t('successCta')}
                    </>
                  ) : (
                    t('resetPasswordCta')
                  )}
                </span>
              </ButtonWrapper>
            </form>

            {/* Back to login */}
            <div className="mt-8 text-center">
              <p className="text-gray-500 text-base">
                {t('rememberPassword')}{' '}
                <Link to="/login" className="text-nokia-blue font-bold hover:text-primary-hover transition-all">
                  {t('signIn')}
                </Link>
              </p>
            </div>
          </FormContainer>

          {/* Decorative corners */}
          <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-nokia-blue rounded-tl-2xl opacity-40 group-hover/card:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
          <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-nokia-green rounded-br-2xl opacity-40 group-hover/card:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
        </div>

      </main>
    </>
  );
};

export default ResetPassword;
